import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { ChevronRightIcon } from "@/components/appIcons";
import { DealOfferExplorerModal } from "@/components/marketplace/deal-offer-explorer-modal";
import { DealOfferStoreDisclosure } from "@/components/marketplace/deal-offer-panel-footer";
import {
  getDealOffersQueryOptions,
  type DealOffersQueryOptionsInput,
} from "@/components/marketplace/deal-offers-query";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { OfferRow } from "@/components/marketplace/offer-row";
import { selectReleaseOffers } from "@/components/marketplace/selectReleaseOffers";
import { Price } from "@/components/Price";
import { Button } from "@/components/ui/button";
import { useHashModal } from "@/hooks/use-hash-modal";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";

function UsedOfferGroup({
  offers,
  heading,
  currency,
  contextReleaseId,
}: {
  offers: DealOffer[];
  heading?: string;
  currency: string;
  contextReleaseId?: string;
}) {
  if (!offers.length) return null;
  return (
    <div className="@container border-t border-border">
      {heading && (
        <h3 className="px-4 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:px-6">
          {heading}
        </h3>
      )}
      <ul>
        {offers.map((offer, index) => (
          <OfferRow
            key={offer.offerId}
            offer={offer}
            currency={currency}
            cheapest={index === 0}
            contextReleaseId={contextReleaseId}
          />
        ))}
      </ul>
    </div>
  );
}

function UsedOfferSummary({
  offers,
  otherOffers,
  currency,
  contextReleaseId,
}: {
  offers: DealOffer[];
  otherOffers: DealOffer[];
  currency: string;
  contextReleaseId?: string;
}) {
  const t = useTranslations("shared.affiliates");
  const primary = offers.length ? offers : otherOffers;
  const scope = contextReleaseId ? (offers.length ? "this" : "otherEdition") : "all";
  return (
    <span className="min-w-0">
      {t.rich("usedOffersFrom", {
        scope,
        count: primary.length,
        price: () => (
          <Price amount={primary[0]!.currentPrice} currency={currency} className="text-primary" />
        ),
      })}
      {offers.length > 0 && otherOffers.length > 0 && (
        <span className="mt-1 block text-xs font-normal text-muted-foreground">
          {t.rich("otherUsedOffersFrom", {
            count: otherOffers.length,
            price: () => (
              <Price
                amount={otherOffers[0]!.currentPrice}
                currency={currency}
                className="text-primary"
              />
            ),
          })}
        </span>
      )}
    </span>
  );
}

function UsedOffersContent({
  input,
  offers,
  hasNewOffers,
  contextReleaseId,
  showStoreDisclosure,
}: {
  input: DealOffersQueryOptionsInput;
  offers: DealOffer[];
  hasNewOffers: boolean;
  contextReleaseId?: string;
  showStoreDisclosure: boolean;
}) {
  const t = useTranslations("shared.affiliates");
  const modal = useHashModal();
  const releaseId = contextReleaseId ?? input.releaseId;
  const selection = releaseId ? selectReleaseOffers(offers, releaseId) : null;
  const primaryOffers = selection ? selection.releaseOffers : offers;
  const otherOffers = selection ? selection.otherOffers : [];
  const primaryVisible = primaryOffers.slice(0, 3);
  const otherVisible = otherOffers.slice(0, 3 - primaryVisible.length);
  const firstOffer = primaryOffers[0] ?? otherOffers[0]!;
  const ownerId = `used:${releaseId ? `release:${releaseId}` : `work:${input.workId}`}`;
  return (
    <>
      <section className="mt-3 overflow-hidden rounded-2xl border border-border bg-card">
        <details open={!hasNewOffers} className="group/used">
          <summary className="flex cursor-pointer list-none items-start gap-2 px-4 py-3 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-primary sm:px-6 [&::-webkit-details-marker]:hidden">
            <ChevronRightIcon
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-base transition-transform group-open/used:rotate-90 motion-reduce:transition-none"
            />
            <UsedOfferSummary
              offers={primaryOffers}
              otherOffers={otherOffers}
              currency={input.currency}
              contextReleaseId={releaseId}
            />
          </summary>
          <UsedOfferGroup
            offers={primaryVisible}
            heading={releaseId ? t("thisEditionOffers") : undefined}
            currency={input.currency}
            contextReleaseId={releaseId}
          />
          <UsedOfferGroup
            offers={otherVisible}
            heading={t("otherEditionOffers")}
            currency={input.currency}
            contextReleaseId={releaseId}
          />
          {offers.length > 3 && (
            <div className="border-t border-border px-4 py-2 sm:px-6">
              <Button
                variant="link"
                className="px-0 text-xs"
                aria-haspopup="dialog"
                onClick={() =>
                  modal.open(
                    "offer-explorer",
                    {
                      offer: firstOffer,
                      condition: "used",
                      format: input.format === "all" ? undefined : input.format,
                    },
                    { ownerId },
                  )
                }
              >
                {t("viewAllUsedOffers")}
              </Button>
            </div>
          )}
        </details>
        <DealOfferExplorerModal
          offers={offers}
          releaseId={input.releaseId}
          contextReleaseId={releaseId}
          workId={input.workId}
          currency={input.currency}
          ownerId={ownerId}
          queryInput={input}
          condition="used"
        />
      </section>
      {showStoreDisclosure && <DealOfferStoreDisclosure />}
    </>
  );
}

export function UsedOfferSection({
  input,
  hasNewOffers,
  contextReleaseId,
  showStoreDisclosure = !hasNewOffers,
}: {
  input: DealOffersQueryOptionsInput;
  hasNewOffers: boolean;
  contextReleaseId?: string;
  showStoreDisclosure?: boolean;
}) {
  const t = useTranslations("shared.affiliates");
  const { viewerId } = useViewerQueryState();
  const queryInput = { ...input, condition: "used" as const };
  const query = useQuery(getDealOffersQueryOptions(queryInput, { viewerId }));
  const offers = (query.data?.offers ?? []) as DealOffer[];
  if (query.isLoading)
    return (
      <p className="px-4 py-3 text-xs text-muted-foreground" role="status">
        {t("usedOffersLoading")}
      </p>
    );
  if (query.isError)
    return (
      <div className="px-4 py-3 text-sm text-muted-foreground">
        {t("usedOffersError")}{" "}
        <Button variant="ghost" onClick={() => void query.refetch()}>
          {t("usedOffersRetry")}
        </Button>
      </div>
    );
  if (!offers.length) return null;
  return (
    <UsedOffersContent
      key={contextReleaseId ?? input.releaseId ?? input.workId}
      input={queryInput}
      offers={offers}
      hasNewOffers={hasNewOffers}
      contextReleaseId={contextReleaseId}
      showStoreDisclosure={showStoreDisclosure}
    />
  );
}
